// ============================================================================
// Annual increase — the screen's pure helpers. Item #706, 2026-10-08.
//
// ⛔ NOTHING HERE DECIDES A DATE. Bryan's rule (day 1-7 → the 1st of that month,
// day 8+ → the 1st of the next) lives in ONE place, the database function
// annual_increase_date(), and the next increase in annual_increase_next_date().
// The window asks the database for both (PLAYBOOK §10d3, TCSD-0341). What is
// here is display: which anniversary a stored date belongs to, rounding a
// proposed figure, reading the record of a line's last change, and summing a
// booked increase for the account card.
// ============================================================================

/** ISO yyyy-mm-dd in local time, no timezone drift. */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function parseIso(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * The contract anniversary a stored increase date belongs to: the contract
 * start's month and day, in whichever year puts it nearest the stored date.
 * A deferral moves the date, never the anniversary.
 */
export function anniversaryNear(contractStart: string | null, around: string): string | null {
  if (!contractStart) return null;
  const cs = parseIso(contractStart);
  const at = parseIso(around);
  let best: Date | null = null;
  for (const dy of [-1, 0, 1]) {
    const y = at.getFullYear() + dy;
    // Feb 29 in a common year falls back to Feb 28, as Postgres does.
    const lastDay = new Date(y, cs.getMonth() + 1, 0).getDate();
    const cand = new Date(y, cs.getMonth(), Math.min(cs.getDate(), lastDay));
    if (cand <= cs) continue;
    if (!best || Math.abs(+cand - +at) < Math.abs(+best - +at)) best = cand;
  }
  return best ? isoDate(best) : null;
}

/**
 * Item #710. The contract anniversary a building's FIRST increase is counted
 * from: contract start + k years, for the smallest k ≥ 1 that lands on or after
 * `today`. Plain date arithmetic — Feb 29 in a common year falls back to Feb 28,
 * as Postgres and anniversaryNear() do. The increase date itself is then asked
 * of the database's annual_increase_date() (Bryan's rule never lives here).
 *
 * ⛔ Not annual_increase_next_date(): that one finds the anniversary NEAREST a
 * start date and returns the one after it — right for Validate, a year late here
 * whenever the coming anniversary is nearer than the last (Menorah → 2028).
 */
export function nextAnniversaryOnOrAfter(contractStart: string | null, today: string): string | null {
  if (!contractStart) return null;
  const cs = parseIso(contractStart);
  const at = parseIso(today);
  for (let k = 1; k < 200; k += 1) {
    const y = cs.getFullYear() + k;
    const lastDay = new Date(y, cs.getMonth() + 1, 0).getDate();
    const cand = new Date(y, cs.getMonth(), Math.min(cs.getDate(), lastDay));
    if (cand >= at) return isoDate(cand);
  }
  return null;
}

/** The day after `today`, for the date box's minimum (defer_kicker refuses today or earlier). */
export function dayAfter(today: string): string {
  const d = parseIso(today);
  d.setDate(d.getDate() + 1);
  return isoDate(d);
}

export type FirstDateGroup = 'New accounts' | 'Hospitals' | 'Other';

/** Item #710's grouping: a monthly rate above zero → New accounts; else a hospital → Hospitals; else Other. */
export function firstDateGroup(monthlyRate: number | null, facilityType: string | null): FirstDateGroup {
  if (monthlyRate != null && Number(monthlyRate) > 0) return 'New accounts';
  if (facilityType === 'Hospital') return 'Hospitals';
  return 'Other';
}

const FIRST_DATE_NOTE = 'First increase date, set from "Needs a first increase date".';

/** The fixed note filed with the first date (TCSD-0378: filed as Deferred, with a note saying it is the first date). */
export function firstDateNote(prefill: string | null, anniversary: string | null, chosen: string): string {
  if (!prefill) return `${FIRST_DATE_NOTE} No contract start on record; date typed.`;
  if (chosen === prefill) return `${FIRST_DATE_NOTE} Pre-filled ${prefill} from the ${anniversary} anniversary.`;
  return `${FIRST_DATE_NOTE} Pre-filled ${prefill}; set to ${chosen}.`;
}

export type Rounding = 'exact' | 'dollar' | 'five' | 'twentyfive';

export const ROUNDING_LABELS: Record<Rounding, string> = {
  exact: 'Exact',
  dollar: 'Whole $',
  five: 'Nearest $5',
  twentyfive: 'Nearest $25',
};

export function roundRate(n: number, mode: Rounding): number {
  switch (mode) {
    case 'dollar':
      return Math.round(n);
    case 'five':
      return Math.round(n / 5) * 5;
    case 'twentyfive':
      return Math.round(n / 25) * 25;
    default:
      return Math.round(n * 100) / 100;
  }
}

/** current × (1 + pct), to the cent. pct is a fraction (0.03). */
export function withIncrease(current: number, pct: number | null): number {
  return Math.round(current * (1 + (pct ?? 0)) * 100) / 100;
}

export interface RateRecord {
  proposal_id: string;
  target_table: string;
  target_row_id: string | null;
  field_name: string;
  current_value: number | null;
  proposed_value: number;
  status: string;
  effective_date: string | null;
  created_at: string;
  kv_id: string | null;
  reason: string;
}

export interface AuditChange {
  record_id: string;
  changed_at: string;
  old_rate: number | null;
  new_rate: number | null;
}

export interface LastChange {
  from: number | null;
  to: number;
  /** The date the change took effect, or null where the record never had one. */
  on: string | null;
  /** When it was written, for a record with no effective date. */
  recordedOn: string;
  source: 'line' | 'building' | 'outside';
  annualIncrease: boolean;
  id: string | null;
}

/**
 * Decision 3 = A: a line's last rate change on record, read-only. The line's
 * own records first; then a pre-2026-09-08 record on the building's rate (they
 * were written before rates lived on lines, so on a one-line building they
 * describe that line); then the change log, labelled as made outside the app.
 */
export function lastChangeFor(
  assignmentId: string,
  lineCount: number,
  records: RateRecord[],
  audits: AuditChange[],
): LastChange | null {
  const usable = records
    .filter((r) => r.status === 'Applied' || r.status === 'Approved')
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  const own = usable.find(
    (r) =>
      r.target_table === 'service_assignments' &&
      r.target_row_id === assignmentId &&
      r.field_name === 'billing_rate',
  );
  const pick =
    own ??
    (lineCount === 1
      ? usable.find((r) => r.target_table === 'facilities' && r.field_name === 'monthly_rate')
      : undefined);
  if (pick) {
    return {
      from: pick.current_value,
      to: pick.proposed_value,
      on: pick.effective_date,
      recordedOn: pick.created_at.slice(0, 10),
      source: own ? 'line' : 'building',
      annualIncrease: pick.kv_id != null,
      id: pick.proposal_id,
    };
  }
  const a = audits
    .filter((x) => x.record_id === assignmentId && x.old_rate != null && x.new_rate != null)
    .sort((x, y) => (x.changed_at < y.changed_at ? 1 : -1))[0];
  if (!a || a.new_rate == null) return null;
  return {
    from: a.old_rate,
    to: a.new_rate,
    on: a.changed_at.slice(0, 10),
    recordedOn: a.changed_at.slice(0, 10),
    source: 'outside',
    annualIncrease: false,
    id: null,
  };
}

export function pctChange(from: number | null, to: number): number | null {
  if (from == null || from === 0) return null;
  return ((to - from) / from) * 100;
}

export interface ScheduledIncrease {
  on: string;
  newTotal: number;
}

/**
 * A booked annual increase on the account card: the earliest night with a
 * Scheduled change that belongs to an annual increase, and the card total once
 * it lands (today's card plus every line's step on that night).
 */
export function scheduledIncreaseFrom(
  records: Pick<
    RateRecord,
    'status' | 'kv_id' | 'field_name' | 'effective_date' | 'current_value' | 'proposed_value'
  >[],
  currentCard: number | null,
): ScheduledIncrease | null {
  const booked = records.filter(
    (r) =>
      r.status === 'Scheduled' && r.kv_id != null && r.field_name === 'billing_rate' && !!r.effective_date,
  );
  if (booked.length === 0 || currentCard == null) return null;
  const on = booked.map((r) => r.effective_date as string).sort()[0];
  const step = booked
    .filter((r) => r.effective_date === on)
    .reduce((s, r) => s + (Number(r.proposed_value) - Number(r.current_value ?? 0)), 0);
  return { on, newTotal: Math.round((Number(currentCard) + step) * 100) / 100 };
}

export function fmtMoney2(n: number | null): string {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function fmtLongDate(s: string | null): string {
  if (!s) return '—';
  return parseIso(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function fmtShortDate(s: string | null): string {
  if (!s) return '—';
  return parseIso(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
